import { confirm, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { inferTaxRateFromDocumentTotals } from "@/modules/quotation-invoice/domain/line-totals";
import type { CreateDocumentItemDTO } from "@/modules/quotation-invoice/domain/types";
import { getStockOrderDetailUrl } from "@/shared/lib/stock-app-url";
import {
  useConvertMyQuotationToInvoice,
  useConvertMyQuotationToSalesOrder,
  useDeleteMyDocument,
  useMyDocumentDetailQuery,
  useUpdateMyDocumentStatus,
} from "../../api/queries";
import { ConvertInvoicePeriodDialog } from "../../ui/ConvertInvoicePeriodDialog";
import {
  ConvertQuotationToSalesOrderErrorDialog,
  type ConvertSalesOrderInvalidLine,
} from "../../ui/ConvertQuotationToSalesOrderErrorDialog";
import { DocumentDetailContent } from "../../ui/DocumentDetailContent";
import { IssueQuotationStockPrecheckDialog } from "../../ui/IssueQuotationStockPrecheckDialog";
import { useQuotationIssueWithPrecheck } from "../../lib/useQuotationIssueWithPrecheck";

export function MyDocumentDetailPage() {
  const { id } = useParams({ from: "/app/my-documents/$id" });
  const nav = useNavigate({ from: "/app/my-documents/$id" });
  const { data, isLoading } = useMyDocumentDetailQuery(id ?? "");
  const convertInvoice = useConvertMyQuotationToInvoice();
  const convertSalesOrder = useConvertMyQuotationToSalesOrder();
  const deleteDocument = useDeleteMyDocument();
  const updateStatus = useUpdateMyDocumentStatus();
  const issuePrecheck = useQuotationIssueWithPrecheck();
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertSoErrorOpen, setConvertSoErrorOpen] = useState(false);
  const [convertSoInvalidLines, setConvertSoInvalidLines] = useState<
    ConvertSalesOrderInvalidLine[]
  >([]);

  const handleBack = () =>
    nav({
      to: "/app/my-documents",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        filters: undefined,
      },
    });

  const handleDownload = () => {
    if (id) {
      nav({
        to: "/app/my-documents/$id/pdf-preview",
        params: { id },
        search: { print: undefined },
      });
    }
  };

  const handlePrint = () => {
    if (id) {
      nav({
        to: "/app/my-documents/$id/pdf-preview",
        params: { id },
        search: { print: 1 },
      });
    }
  };

  const doc = data?.document;

  const defaultItemsForConvert = useMemo((): CreateDocumentItemDTO[] => {
    if (!data?.items?.length) return [];
    return data.items.map((it) => ({
      description: it.description,
      detail: it.detail ?? null,
      quantity: it.quantity,
      unit: it.unit,
      unitPrice: it.unitPrice,
      discountAmount: it.discountAmount,
      taxRate: it.taxRate,
    }));
  }, [data?.items]);

  const defaultTaxRate = useMemo(() => {
    if (!doc) return 0.1;
    return inferTaxRateFromDocumentTotals({
      subtotal: doc.subtotal,
      discountAmount: doc.discountAmount,
      taxAmount: doc.taxAmount,
    });
  }, [doc]);

  const needsConvertDialog = (data?.childInvoices?.length ?? 0) > 0;

  const handleIssueDocument =
    data && doc && doc.status === "draft"
      ? () => {
          issuePrecheck.requestIssue({
            id: doc.id,
            documentNumber: doc.documentNumber ?? doc.id.slice(0, 8),
            documentType: doc.documentType,
            onIssue: async () => {
              try {
                await updateStatus.mutateAsync({
                  id: doc.id,
                  status: "issued",
                });
                toast.success("ອອກເອກະສານສຳເລັດ");
              } catch {
                /* onError ຈາກ mutation */
              }
            },
          });
        }
      : undefined;

  const handleCancelDocument =
    data && doc && doc.status !== "cancelled"
      ? async () => {
          const ok = await confirm({
            title: "ຍົກເລີກເອກະສານ",
            description: `ຍົກເລີກເອກະສານ #${doc.documentNumber ?? doc.id.slice(0, 8)}?`,
            actionText: "ຍົກເລີກ",
            ActionProps: { variant: "destructive" },
          });
          if (!ok) return;
          try {
            await updateStatus.mutateAsync({
              id: doc.id,
              status: "cancelled",
            });
            toast.success("ຍົກເລີກສຳເລັດ");
          } catch {
            /* onError ຈາກ mutation */
          }
        }
      : undefined;

  const canConvertQuotation =
    doc?.documentType === "quotation" &&
    doc.status === "issued" &&
    (data?.quotationHeadroom?.remaining ?? 0) > 0.001;

  const canConvertToSalesOrder =
    doc?.documentType === "quotation" &&
    doc.status === "issued" &&
    !data?.linkedSalesOrder;

  return (
    <>
      <Header />
      <Main>
        {isLoading && <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>}
        {data &&
          doc &&
          doc.documentType === "quotation" &&
          data.quotationHeadroom && (
            <ConvertInvoicePeriodDialog
              open={convertDialogOpen}
              onOpenChange={setConvertDialogOpen}
              quotationHeadroom={data.quotationHeadroom}
              defaultItems={defaultItemsForConvert}
              defaultDiscount={doc.discountAmount}
              defaultTaxRate={defaultTaxRate}
              isSubmitting={convertInvoice.isPending}
              onSubmit={(body) => {
                convertInvoice.mutate(
                  { id: doc.id, body },
                  {
                    onSuccess: (created) => {
                      setConvertDialogOpen(false);
                      nav({
                        to: "/app/my-documents/$id",
                        params: { id: created.id },
                      });
                    },
                  },
                );
              }}
            />
          )}
        {data && doc && (
          <DocumentDetailContent
            document={doc}
            items={data.items}
            history={data.history}
            payments={data.payments}
            childInvoices={data.childInvoices}
            quotationHeadroom={data.quotationHeadroom ?? null}
            linkedSalesOrder={data.linkedSalesOrder ?? null}
            stockOrderDetailUrl={
              data.linkedSalesOrder
                ? getStockOrderDetailUrl(data.linkedSalesOrder.id)
                : null
            }
            portalScope
            onOpenChildInvoice={(childId) =>
              nav({
                to: "/app/my-documents/$id",
                params: { id: childId },
              })
            }
            onBack={handleBack}
            onDownload={handleDownload}
            onPrint={handlePrint}
            onEdit={
              doc.status === "draft"
                ? () =>
                    nav({
                      to: "/app/my-documents/$id/edit",
                      params: { id: doc.id },
                    })
                : undefined
            }
            onDelete={
              doc.status === "draft" || doc.status === "cancelled"
                ? async () => {
                    const ok = await confirm({
                      title: "ລຶບເອກະສານ",
                      description: `ລຶບເອກະສານ #${doc.documentNumber ?? doc.id.slice(0, 8)}?`,
                      actionText: "ລຶບ",
                      ActionProps: { variant: "destructive" },
                    });
                    if (ok) {
                      await deleteDocument.mutateAsync(doc.id);
                      handleBack();
                    }
                  }
                : undefined
            }
            onIssueDocument={handleIssueDocument}
            onCancelDocument={handleCancelDocument}
            statusActionPending={updateStatus.isPending}
            onConvertToSalesOrder={
              canConvertToSalesOrder
                ? async () => {
                    const ok = await confirm({
                      title: "ແປງເປັນຄຳສັ່ງຂາຍ",
                      description: `ຢືນຢັນແປງລາຄາ #${doc.documentNumber ?? doc.id.slice(0, 8)} ເປັນຄຳສັ່ງຂາຍ (draft)?`,
                      actionText: "ແປງ",
                    });
                    if (!ok) return;
                    convertSalesOrder.mutate(doc.id, {
                      onSuccess: (result) => {
                        window.open(
                          getStockOrderDetailUrl(result.salesOrderId),
                          "_blank",
                          "noopener,noreferrer",
                        );
                      },
                      onError: (err: unknown) => {
                        const e = err as Error & {
                          code?: string;
                          invalidLines?: ConvertSalesOrderInvalidLine[];
                        };
                        if (
                          e.code === "INVALID_LINES" &&
                          e.invalidLines?.length
                        ) {
                          setConvertSoInvalidLines(e.invalidLines);
                          setConvertSoErrorOpen(true);
                        }
                      },
                    });
                  }
                : undefined
            }
            convertToSalesOrderPending={convertSalesOrder.isPending}
            onConvertToInvoice={
              canConvertQuotation
                ? () => {
                    if (needsConvertDialog) {
                      setConvertDialogOpen(true);
                    } else {
                      convertInvoice.mutate(
                        { id: doc.id },
                        {
                          onSuccess: (created) => {
                            nav({
                              to: "/app/my-documents/$id",
                              params: { id: created.id },
                            });
                          },
                        },
                      );
                    }
                  }
                : undefined
            }
          />
        )}
        <ConvertQuotationToSalesOrderErrorDialog
          open={convertSoErrorOpen}
          onOpenChange={setConvertSoErrorOpen}
          invalidLines={convertSoInvalidLines}
        />
        <IssueQuotationStockPrecheckDialog {...issuePrecheck.precheckDialogProps} />
        {!data && !isLoading && (
          <p className="text-muted-foreground">ບໍ່ພົບເອກະສານ</p>
        )}
      </Main>
    </>
  );
}
